import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, MapPin, Map, X, Eye, EyeOff, Navigation, RefreshCw, Loader2 } from 'lucide-react';
import { getAllZones, createZone, updateZone, deleteZone } from '../services/zoneApi';
import type { Zone } from '../services/zoneApi';
import { useI18n } from '../contexts/I18nContext';
import ImprovedTable from '../components/ImprovedTable';
import Pagination from '../components/Pagination';
import ZoneMap from '../components/ZoneMap';
import { useCurrentLocation } from '../hooks/useCurrentLocation';

const Zonas: React.FC = () => {
  const { t } = useI18n();
  const [zones, setZones] = useState<Zone[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [showMap, setShowMap] = useState(true);
  const { location: currentLocation, loading: locationLoading, error: locationError, refresh: refreshLocation } =
    useCurrentLocation(showMap);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    center_lat: '',
    center_lon: '',
    radius: '',
    boundaries: '',
  });

  const loadZones = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllZones(false); // Carregar todas as zonas (ativas e inativas)
      setZones(data);
      setCurrentPage(1);
    } catch (error) {
      console.error('Erro ao carregar zonas:', error);
      alert(t('zones.error.loading') || 'Erro ao carregar zonas');
    }
    setIsLoading(false);
  }, [t]);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      center_lat: '',
      center_lon: '',
      radius: '',
      boundaries: '',
    });
    setEditingZone(null);
    setShowForm(false);
  }, []);

  const handleEdit = useCallback((zone: Zone) => {
    setEditingZone(zone);
    setFormData({
      name: zone.name,
      description: zone.description || '',
      center_lat: zone.center_lat.toString(),
      center_lon: zone.center_lon.toString(),
      radius: zone.radius?.toString() || '',
      boundaries: zone.boundaries || '',
    });
    setShowForm(true);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.center_lat || !formData.center_lon) {
      alert(t('zones.fillNameAndCoords'));
      return;
    }

    setIsLoading(true);
    try {
      const zoneData = {
        name: formData.name,
        description: formData.description || undefined,
        center_lat: parseFloat(formData.center_lat),
        center_lon: parseFloat(formData.center_lon),
        radius: formData.radius ? parseFloat(formData.radius) : undefined,
        boundaries: formData.boundaries || undefined,
      };

      if (editingZone) {
        await updateZone(editingZone.id, zoneData);
        alert(t('zones.success.updated') || 'Zona atualizada com sucesso!');
      } else {
        await createZone(zoneData);
        alert(t('zones.success.created') || 'Zona criada com sucesso!');
      }
      
      resetForm();
      await loadZones();
    } catch (error: any) {
      console.error('Erro ao salvar zona:', error);
      alert(error.response?.data?.error || (t('zones.error.saving') || 'Erro ao salvar zona'));
    }
    setIsLoading(false);
  }, [formData, editingZone, t, resetForm, loadZones]);

  const handleDelete = useCallback(async (zone: Zone) => {
    if (!confirm(t('zones.confirm.delete') || `Tem certeza que deseja desativar a zona "${zone.name}"?`)) {
      return;
    }

    setIsLoading(true);
    try {
      await deleteZone(zone.id);
      alert(t('zones.success.deleted') || 'Zona desativada com sucesso!');
      await loadZones();
    } catch (error: any) {
      console.error('Erro ao deletar zona:', error);
      alert(error.response?.data?.error || (t('zones.error.deleting') || 'Erro ao deletar zona'));
    }
    setIsLoading(false);
  }, [t, loadZones]);

  // Paginação
  const paginatedZones = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return zones.slice(startIndex, endIndex);
  }, [zones, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(zones.length / itemsPerPage);

  // Colunas da tabela
  const columns = React.useMemo(() => [
    {
      key: 'name',
      label: t('zones.name') || 'Nome',
      render: (zone: Zone) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MapPin size={16} color="#3b82f6" />
          <span style={{ fontWeight: 500 }}>{zone.name}</span>
          {!zone.is_active && (
            <span style={{
              fontSize: '12px',
              color: '#6b7280',
              backgroundColor: '#f3f4f6',
              padding: '2px 8px',
              borderRadius: '4px',
            }}>Inativa</span>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      label: t('zones.description') || 'Descrição',
      render: (zone: Zone) => zone.description || '-',
    },
    {
      key: 'center',
      label: t('zones.center') || 'Centro',
      render: (zone: Zone) => (
        <span style={{ fontSize: '14px' }}>
          {zone.center_lat.toFixed(6)}, {zone.center_lon.toFixed(6)}
        </span>
      ),
    },
    {
      key: 'radius',
      label: t('zones.radius') || 'Raio (km)',
      render: (zone: Zone) => zone.radius ? `${zone.radius} km` : '-',
    },
    {
      key: 'actions',
      label: t('actions') || 'Ações',
      render: (zone: Zone) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => handleEdit(zone)}
            style={{
              padding: '8px',
              color: '#2563eb',
              background: 'transparent',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            title={t('edit') || 'Editar'}
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => handleDelete(zone)}
            style={{
              padding: '8px',
              color: '#dc2626',
              background: 'transparent',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            title={t('delete') || 'Deletar'}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ], [t, handleEdit, handleDelete]);

  return (
    <div style={{ padding: '30px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', color: '#333', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '24px' }}>
            <Map size={24} />
            {t('zones.title') || 'Gerenciamento de Zonas'}
          </h1>
          <p style={{ color: '#666', margin: 0 }}>
            {t('zones.subtitle') || 'Gerencie as zonas geográficas de atuação'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowMap(!showMap)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#4b5563',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            {showMap ? <EyeOff size={18} /> : <Eye size={18} />}
            {showMap ? (t('zones.hideMap') || 'Ocultar Mapa') : (t('zones.showMap') || 'Mostrar Mapa')}
          </button>
          <button
            onClick={() => setShowForm(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            <Plus size={18} />
            {t('zones.create') || 'Nova Zona'}
          </button>
        </div>
      </div>

      {showMap && (
        <div style={{
          marginBottom: '24px',
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '20px',
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '16px',
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#333' }}>
              {t('zones.mapView') || 'Visualização no Mapa'}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
              {locationLoading && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280' }}>
                  <Loader2 size={16} className="spin" />
                  {t('zones.currentLocation.loading') || 'Obtendo localização...'}
                </span>
              )}
              {!locationLoading && currentLocation && (
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#eff6ff',
                  color: '#1e40af',
                  padding: '8px 12px',
                  borderRadius: '8px',
                }}>
                  <Navigation size={16} />
                  <span>
                    {currentLocation.city
                      ? `${currentLocation.city}${currentLocation.region ? `, ${currentLocation.region}` : ''}`
                      : `${currentLocation.lat.toFixed(4)}, ${currentLocation.lon.toFixed(4)}`}
                    {' · '}
                    {currentLocation.source === 'gps'
                      ? (t('zones.currentLocation.gps') || 'GPS')
                      : (t('zones.currentLocation.ip') || 'Aproximado (IP)')}
                  </span>
                  <button
                    type="button"
                    onClick={refreshLocation}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title={t('zones.currentLocation.refresh') || 'Atualizar localização'}
                  >
                    <RefreshCw size={14} />
                  </button>
                </span>
              )}
              {!locationLoading && locationError && (
                <span style={{ color: '#b45309', fontSize: '14px' }}>
                  {t('zones.currentLocation.error') || 'Localização indisponível'}
                </span>
              )}
            </div>
          </div>
          <ZoneMap
            zones={zones}
            selectedZone={selectedZone}
            onZoneClick={(zone) => {
              setSelectedZone(zone);
              handleEdit(zone);
            }}
            currentLocation={currentLocation}
            height="420px"
          />
        </div>
      )}

      {showForm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: '16px',
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '24px',
            width: '100%',
            maxWidth: '640px',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>
                {editingZone ? (t('zones.edit') || 'Editar Zona') : (t('zones.create') || 'Nova Zona')}
              </h2>
              <button
                onClick={resetForm}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
                  {t('zones.name') || 'Nome'} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                  required
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
                  {t('zones.description') || 'Descrição'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical',
                  }}
                  rows={3}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
                    {t('zones.centerLat') || 'Latitude do Centro'} *
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.center_lat}
                    onChange={(e) => setFormData({ ...formData, center_lat: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                    required
                    placeholder="Ex: -23.550520"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
                    {t('zones.centerLon') || 'Longitude do Centro'} *
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.center_lon}
                    onChange={(e) => setFormData({ ...formData, center_lon: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                    required
                    placeholder="Ex: -46.633308"
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
                  {t('zones.radius') || 'Raio (km)'}
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.radius}
                  onChange={(e) => setFormData({ ...formData, radius: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                  placeholder="Ex: 5.0 (para zonas circulares)"
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
                  {t('zones.radiusHelp') || 'Deixe vazio para usar polígono (boundaries)'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
                  {t('zones.boundaries') || 'Boundaries (JSON)'}
                </label>
                <textarea
                  value={formData.boundaries}
                  onChange={(e) => setFormData({ ...formData, boundaries: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    resize: 'vertical',
                  }}
                  rows={4}
                  placeholder='{"type": "Polygon", "coordinates": [[[lat1, lon1], [lat2, lon2], ...]]}'
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
                  {t('zones.boundariesHelp') || 'Formato GeoJSON para definir polígono da zona'}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.6 : 1,
                    fontWeight: 500,
                  }}
                >
                  {isLoading ? (t('saving') || 'Salvando...') : (t('save') || 'Salvar')}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    flex: 1,
                    backgroundColor: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  {t('cancel') || 'Cancelar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isLoading && zones.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div className="spin" style={{
            display: 'inline-block',
            width: '32px',
            height: '32px',
            border: '2px solid #e5e7eb',
            borderTopColor: '#2563eb',
            borderRadius: '50%',
          }} />
          <p style={{ marginTop: '16px', color: '#6b7280' }}>{t('loading') || 'Carregando...'}</p>
        </div>
      ) : zones.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          backgroundColor: '#f9fafb',
          borderRadius: '10px',
          border: '1px solid #e5e7eb',
        }}>
          <MapPin size={48} color="#9ca3af" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: '#6b7280', margin: 0, fontSize: '16px' }}>
            {t('zones.empty') || 'Nenhuma zona cadastrada'}
          </p>
        </div>
      ) : (
        <>
          <ImprovedTable
            data={paginatedZones}
            columns={columns}
            isLoading={isLoading}
          />
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
              totalItems={zones.length}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Zonas;

